import {
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  MenuItem as SelectMenuItem,
  TextField,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendIcon from "@mui/icons-material/Send";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import { FC, useContext, useEffect, useRef, useState } from "react";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import usePopup from "../../../hooks/usePopup";
import PriorApproval from "./PriorApproval";
import ConsentFormGrants from "../ConsenForms/grantsforconsentform";
import {
  createTelemedicine,
  sendTreatmentEstimate,
  TelemedicineProvider,
} from "../../services";
import { clinicId } from "../../../constants";
import { EmrContext } from "../..";
import { CustomFormField } from "../../../utilis/component/CustomFormElement";
import useAuthentication from "@hooks/useAuthentication";
import usePrint from "../../../hooks/usePrint";

type TelemedicineFormValues = {
  patientName: string;
  patientEmail: string;
  doctorEmail: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  provider: TelemedicineProvider;
};

const ButtonContainer: FC = () => {
  const { CustomModal, showSnackbar } = usePopup();
  const { encounterId } = useParams();
  const { patientData } = useContext(EmrContext);
  const { user } = useAuthentication();
  const { printPdf } = usePrint();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [showPriorRequest, setShowPriorRequest] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [showConsent, setShowConsent] = useState<boolean>(false);
  const [showTelemedicine, setShowTelemedicine] = useState(false);
  const telemedicineWindowRef = useRef<Window | null>(null);

  const openMeetingInNewWindow = (url: string) => {
    const pendingWindow = telemedicineWindowRef.current;
    if (pendingWindow && !pendingWindow.closed) {
      pendingWindow.location.href = url;
      pendingWindow.focus();
      telemedicineWindowRef.current = null;
      return;
    }

    const openedWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (!openedWindow) {
      window.location.assign(url);
    }
    telemedicineWindowRef.current = null;
  };

  const closePendingMeetingWindow = () => {
    telemedicineWindowRef.current?.close();
    telemedicineWindowRef.current = null;
  };

  const { control, handleSubmit, reset, watch, clearErrors } =
    useForm<TelemedicineFormValues>({
      defaultValues: {
        patientName: "",
        patientEmail: "",
        doctorEmail: "",
        startTime: null,
        endTime: null,
        provider: "GOOGLE",
      },
    });
  const selectedProvider = watch("provider");
  const isGoogleProvider = selectedProvider === "GOOGLE";

  useEffect(() => {
    if (!showTelemedicine) return;
    reset({
      patientName: patientData?.pmaster?.patientName || "",
      patientEmail: patientData?.amaster?.email || "",
      doctorEmail: user?.username || "",
      startTime: patientData?.encounter?.startTime
        ? dayjs(patientData.encounter.startTime)
        : dayjs(),
      endTime: patientData?.encounter?.endTime
        ? dayjs(patientData.encounter.endTime)
        : dayjs().add(30, "minute"),
      provider: "GOOGLE",
    });
  }, [showTelemedicine, patientData, user, reset]);

  useEffect(() => {
    if (!isGoogleProvider) {
      clearErrors(["patientEmail", "doctorEmail"]);
    }
  }, [isGoogleProvider, clearErrors]);

  const {
    mutate: sendTreatmentEstimateClick,
    isLoading: sendEstimateLoading,
  } = useMutation(sendTreatmentEstimate, {
    onSuccess: (result) => {
      if (result.responseIndicator === "success" && result.response?.invoice) {
        printPdf(result.response.invoice, "new-tab");
      }
      showSnackbar({
        message: result.responseMessage,
        open: true,
        variant:
          result.responseIndicator === "success" ? "success" : "warning",
        duration: 3000,
      });
    },
    onError: (e: any) => {
      showSnackbar({
        message:
          e?.response?.data?.responseMessage || "Something went wrong !",
        open: true,
        variant: "warning",
        duration: 3000,
      });
    },
  });

  const {
    mutate: startTelemedicine,
    isLoading: telemedicineLoading,
  } = useMutation(createTelemedicine, {
    onSuccess: (result) => {
      if (result.responseIndicator === "success" && result.response?.joinUrl) {
        showSnackbar({
          message: result.responseMessage || "Meeting created successfully",
          open: true,
          variant: "success",
          duration: 3000,
        });
        setShowTelemedicine(false);
        openMeetingInNewWindow(result.response.joinUrl);
      } else {
        closePendingMeetingWindow();
        showSnackbar({
          message: result.responseMessage || "Failed to create meeting",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      closePendingMeetingWindow();
      showSnackbar({
        message:
          e?.response?.data?.responseMessage || "Something went wrong !",
        open: true,
        variant: "warning",
        duration: 3000,
      });
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSendTreatmentEstimate = () => {
    sendTreatmentEstimateClick({
      clinicId,
      encounterId: Number(encounterId),
    });
  };

  const handleOpenTelemedicine = () => {
    setShowTelemedicine(true);
  };

  const onConfirmTelemedicine = (values: TelemedicineFormValues) => {
    if (!values.startTime || !values.endTime) {
      showSnackbar({
        message: "Please select start and end time",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    if (values.endTime.isBefore(values.startTime)) {
      showSnackbar({
        message: "End time must be after start time",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    telemedicineWindowRef.current = window.open("about:blank", "_blank");

    startTelemedicine({
      appointmentId: String(encounterId || ""),
      patientName: values.patientName.trim(),
      patientEmail: values.patientEmail.trim(),
      doctorEmail: values.doctorEmail.trim(),
      startTime: values.startTime.toISOString(),
      endTime: values.endTime.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      provider: values.provider,
    });
  };

  return (
    <div className="flex justify-between items-end pt-1">
      <CustomModal
        title="Prior request"
        open={showPriorRequest}
        setOpen={setShowPriorRequest}
      >
        <div className="w-[50vw]">
          <PriorApproval />
        </div>
      </CustomModal>
      <CustomModal
        title="View details"
        open={showDetails}
        setOpen={setShowDetails}
      >
        <div className="w-[50vw] h-[50dvh]"></div>
      </CustomModal>
      <CustomModal
        title="Consent form"
        open={showConsent}
        setOpen={setShowConsent}
      >
        <ConsentFormGrants />
      </CustomModal>

      <CustomModal
        title="Start telemedicine"
        open={showTelemedicine}
        setOpen={setShowTelemedicine}
      >
        <form
          className="w-[min(90vw,480px)] p-4 flex flex-col gap-4"
          onSubmit={handleSubmit(onConfirmTelemedicine)}
        >
          <CustomFormField
            control={control}
            name="patientName"
            element="input"
            fieldProps={{ label: "Patient name" }}
            rules={{ required: "Patient name is required" }}
          />
          <CustomFormField
            control={control}
            name="patientEmail"
            element="input"
            fieldProps={{ label: "Patient email", type: "email" }}
            rules={{
              required: isGoogleProvider
                ? "Patient email is required for Google Meet"
                : false,
            }}
          />
          <CustomFormField
            control={control}
            name="doctorEmail"
            element="input"
            fieldProps={{ label: "Doctor email", type: "email" }}
            rules={{
              required: isGoogleProvider
                ? "Doctor email is required for Google Meet"
                : false,
            }}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name="startTime"
              control={control}
              rules={{ required: "Start time is required" }}
              render={({ field, fieldState: { error } }) => (
                <DateTimePicker
                  label="Start time"
                  value={field.value}
                  onChange={field.onChange}
                  ampm
                  format="DD-MM-YYYY hh:mm A"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
            <Controller
              name="endTime"
              control={control}
              rules={{ required: "End time is required" }}
              render={({ field, fieldState: { error } }) => (
                <DateTimePicker
                  label="End time"
                  value={field.value}
                  onChange={field.onChange}
                  ampm
                  format="DD-MM-YYYY hh:mm A"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>

          <Controller
            name="provider"
            control={control}
            rules={{ required: "Provider is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Provider"
                error={!!error}
                helperText={error?.message}
              >
                <SelectMenuItem value="GOOGLE">Google Meet</SelectMenuItem>
                <SelectMenuItem value="ZOOM">Zoom</SelectMenuItem>
              </TextField>
            )}
          />

          <div className="flex justify-end gap-2 mt-2">
            <Button
              color="inherit"
              onClick={() => setShowTelemedicine(false)}
              disabled={telemedicineLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={telemedicineLoading}
              startIcon={
                telemedicineLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <VideoCallIcon />
                )
              }
            >
              Confirm
            </Button>
          </div>
        </form>
      </CustomModal>

      <div className="flex gap-2 items-center">
        <IconButton
          sx={{
            width: 25,
            height: 25,
          }}
          onClick={handleClick}
        >
          <MoreVertIcon />
        </IconButton>
      </div>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            setShowConsent(true);
          }}
        >
          <ListItemIcon>
            <SummarizeIcon fontSize="small" />
          </ListItemIcon>
          Consent form
        </MenuItem>

        <MenuItem
          disabled={sendEstimateLoading}
          onClick={handleSendTreatmentEstimate}
        >
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          Treatment estimate
        </MenuItem>

        <MenuItem onClick={handleOpenTelemedicine}>
          <ListItemIcon>
            <VideoCallIcon fontSize="small" />
          </ListItemIcon>
          Start telemedicine
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ButtonContainer;



